import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Users2, Plus, Archive, UserPlus } from "lucide-react";

export default function Teams() {
  const t = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTeamId, setAssignTeamId] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const teams = useQuery({
    queryKey: ["admin-teams"],
    queryFn: async () => (await api.get("/platform/teams")).data,
    retry: false,
  });
  const people = useQuery({
    queryKey: ["admin-people"],
    queryFn: async () => (await api.get("/platform/people")).data,
    retry: false,
  });

  const items = Array.isArray(teams.data) ? teams.data : teams.data?.teams || teams.data?.items || [];
  const peopleList = Array.isArray(people.data) ? people.data : people.data?.people || people.data?.items || [];
  const [selectedUser, setSelectedUser] = useState("");

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/platform/teams", { name: name.trim(), description: description.trim() });
      setName(""); setDescription(""); setOpen(false);
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const archive = async (teamId) => {
    try {
      await api.post(`/platform/teams/${teamId}/archive`);
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const assign = async () => {
    if (!assignTeamId || !selectedUser) return;
    setBusy(true);
    try {
      await api.post(`/platform/teams/${assignTeamId}/move`, { user_id: selectedUser });
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-teams"] });
      qc.invalidateQueries({ queryKey: ["admin-people"] });
      setAssignOpen(false);
      setSelectedUser("");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.teams")}
        title={t("nav.teams")}
        subtitle={t("platform.subtitle")}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="teams-create-open" className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
                <Plus className="h-4 w-4 mr-1" /> {t("platform.createTeam")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="teams-create-dialog">
              <DialogHeader><DialogTitle>{t("platform.createTeam")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>{t("common.name")}</Label>
                  <Input data-testid="teams-create-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-cream-50" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input data-testid="teams-create-desc" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-cream-50" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} data-testid="teams-create-submit" disabled={busy} className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
                  {busy ? t("common.saving") : t("common.save")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {teams.isLoading && <LoadingCard />}
      {teams.isError && <ErrorState message={errMsg(teams.error)} onRetry={() => teams.refetch()} />}
      {!teams.isLoading && !teams.isError && items.length === 0 && (
        <EmptyState icon={Users2} title={t("common.empty")} description="Create your first team to organise your people." testId="teams-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="teams-list">
        {items.map((tm, i) => {
          const id = tm.id || tm._id;
          return (
            <div key={id || i} className="rounded-2xl bg-white shadow-card p-4" data-testid={`teams-row-${i}`}>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-sage-100 flex items-center justify-center shrink-0">
                  <Users2 className="h-4 w-4 text-sage-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-ink-900 truncate">{tm.name || "—"}</div>
                  <div className="text-xs text-ink-500">{tm.description || ""}</div>
                  <div className="text-xs text-sage-700 mt-1">{tm.members_count ?? tm.member_count ?? 0} members</div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-border h-8"
                  data-testid={`teams-assign-${i}`}
                  onClick={() => { setAssignTeamId(id); setAssignOpen(true); }}
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-border h-8 text-rust-600"
                  data-testid={`teams-archive-${i}`}
                  onClick={() => archive(id)}
                >
                  <Archive className="h-3.5 w-3.5 mr-1" /> {t("common.archive")}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="teams-assign-dialog">
          <DialogHeader><DialogTitle>Assign to team</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Employee</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-cream-50" data-testid="teams-assign-user">
                  <SelectValue placeholder="Select an employee…" />
                </SelectTrigger>
                <SelectContent>
                  {peopleList.map((p) => (
                    <SelectItem key={p.id || p.user_id} value={p.id || p.user_id}>
                      {p.name || p.email || p.user_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={assign} data-testid="teams-assign-submit" disabled={busy || !selectedUser} className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
              {busy ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
